import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

import BaseWrapper from '../base-wrapper';
import s from './styles.module.scss';
import { useDraggableTabs } from './use-draggable-tabs';

import type { IBaseComponentProps } from 'infrastructure/interfaces';

export interface IBaseTab<TKey = string | number> extends IBaseComponentProps {
  key: TKey;
  label: string;
  disabled?: boolean;
  closable?: boolean;
  flat?: boolean;
  render?: boolean;
}

export interface IBaseTabsProps<TKey = string | number>
  extends IBaseComponentProps {
  tabs: Array<IBaseTab<TKey>>;
  defaultIndex?: number;
  activeKey?: TKey;
  size?: 'small' | 'default';
  type?: 'rounded' | 'default';
  disabled?: boolean;
  ignoreSingle?: boolean;
  draggable?: boolean;
  contentClassName?: string;
  onChange?: (tab: IBaseTab<TKey>, index: number) => void;
  onCloseTab?: (tab: IBaseTab<TKey>, index: number) => void;
  onReorder?: (from: number, to: number) => void;
  maxWidth?: string;
  wrapperClassName?: string;
}

const safetyIndex = (index: number) => (index < 0 ? 0 : index);

const getActiveTab = (
  tabs: IBaseTabsProps['tabs'],
  activeKey: IBaseTabsProps['activeKey'],
  index: number,
): IBaseTab<any> => {
  if (activeKey) {
    const tab = tabs.find((t) => t.key === activeKey);
    if (tab) return tab;
  }
  return tabs[safetyIndex(index)];
};

const BaseTabs = <TKey extends string | number>(
  props: IBaseTabsProps<TKey>,
) => {
  const {
    tabs,
    onChange,
    onCloseTab,
    onReorder,
    defaultIndex = 0,
    activeKey,
    disabled = false,
    ignoreSingle = false,
    size = 'default',
    type = 'default',
    draggable,
    style,
    className,
    contentClassName,
    wrapperClassName,
    maxWidth,
  } = props;

  const [selectedTab, setSelectedTab] = useState<IBaseTab<TKey> | undefined>(
    getActiveTab(tabs, activeKey, defaultIndex),
  );

  const { dragOverIndex, draggableProps } = useDraggableTabs({
    disabled: disabled || !draggable,
    onReorder,
  });

  const tabsClassNames = classNames(
    s['tabs-wrapper'],
    s[size],
    className,
    {
      [s.disabled]: disabled,
      [s.draggable]: draggable,
    },
    s[type],
  );

  const tabClassNames = (tab: IBaseTab, dragOver: boolean) =>
    classNames(
      tab.className,
      s['tab-item'],
      {
        [s.active]: tab.key === selectedTab?.key,
        [s.disabled]: tab.disabled,
        [s.draggable]: draggable,
        [s.single]: tabs.length === 1 && !ignoreSingle,
        [s.dragOver]: dragOver,
      },
      s[type],
    );

  const contentClassNames = classNames(s.content, contentClassName, {
    [s.disabled]: disabled,
    [s.flat]: selectedTab?.flat,
  });

  const onTabClick = (tab: IBaseTab<TKey>, index: number) => {
    if (dragOverIndex !== null) return;
    setSelectedTab(tab);
    onChange?.(tab, index);
  };

  const handleClose = (tab: IBaseTab<TKey>, index: number) => {
    onCloseTab?.(tab, index);
  };

  useEffect(() => {
    if (tabs.length === 1) {
      setSelectedTab(tabs[0]);
      return;
    }

    setSelectedTab(tabs[safetyIndex(defaultIndex)]);
  }, [defaultIndex]);

  useEffect(() => {
    if (activeKey) {
      const tab = tabs.find((t) => t.key === activeKey);
      if (tab) setSelectedTab(tab);
      else {
        setSelectedTab(undefined);
      }
    }
  }, [activeKey, tabs]);

  useEffect(() => {
    setSelectedTab((prevSelected) => {
      if (!prevSelected) {
        return tabs[safetyIndex(defaultIndex)];
      }

      const sameKeyTab = tabs.find((t) => t.key === prevSelected.key);

      return sameKeyTab ?? tabs[safetyIndex(defaultIndex)];
    });
  }, [tabs, defaultIndex]);

  return (
    <div className={wrapperClassName}>
      <div style={{ width: maxWidth }} className={s['tabs-scroll-container']}>
        <ul style={style} className={tabsClassNames}>
          {tabs.map((tab, index) => (
            <li
              data-cy={`tab-${tab.label}`}
              style={tab.style}
              key={`${tab.key}-${index}`}
              onClick={() => onTabClick(tab, index)}
              className={tabClassNames(tab, dragOverIndex === index)}
              {...(draggable ? draggableProps(index) : {})}
            >
              {tab.label}
              {tab.closable && (
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClose(tab, index);
                  }}
                  className={s.cross}
                >
                  &#10006;
                </span>
              )}
            </li>
          ))}
        </ul>
      </div>
      {selectedTab?.children && (
        <BaseWrapper className={contentClassNames}>
          {selectedTab?.children}
        </BaseWrapper>
      )}
    </div>
  );
};

export default BaseTabs;
